// @ts-check
import { call, select, put, take, takeLatest, cancelled } from 'redux-saga/effects';
import * as Services from 'shared/services';
import { showModal, hideModal } from 'shared/actions/UnboundModalActions';

import {
  REFERRAL_DASHBOARD_PAGE_MOUNTED,
  REFERRAL_DASHBOARD_NEXT_PAGE_REQUESTED,
  REFERRAL_DASHBOARD_SEARCH_SUBMITTED,
  REFERRAL_DASHBOARD_PREVIOUS_PAGE_SLOTS_REQUESTED,
  REFERRAL_DASHBOARD_NEXT_PAGE_SLOTS_REQUESTED,
  REFERRAL_DASHBOARD_OUT_OF_GROUP_REASON_REQUIRED,
  REFERRAL_DASHBOARD_OUT_OF_GROUP_REASON_PROVIDED,
  referralDashboardResultsFetching,
  referralDashboardResultsFetchFailed,
  referralDashboardResultsFetchCancelled,
  referralDashboardResultsReceived,
  referralDashboardPageSlotsFetching,
  referralDashboardPageSlotsReceived,
} from 'shared/actions/ReferralProfiles/ReferralDashboardActions';

/**
 * @param {string} referralProfileId
 * @param {number | void} page
 * @param {string} searchTerm
 * @param {string | void} range
 * @param {string | void} specialtyId
 * @param {"pediatric" | "adult" | void} ageClassification
 */
function* fetchReferralDashboardResults(
  referralProfileId,
  networkId,
  page = null,
  searchTerm = '',
  range = null,
  specialtyId = null,
  ageClassification = null
) {
  const {
    configuration: { apiPrefix },
    pageState,
  } = yield select(state => ({
    configuration: state.configuration,
    pageState: state.referralDashboardResults.pageState,
  }));
  const fetchPage = page || pageState.currentPage;
  const fetchRange = range || pageState.range;
  yield put(referralDashboardResultsFetching(fetchPage, fetchRange));
  try {
    const results = yield call(
      Services.ReferralDashboard.fetchReferralDashboardResults,
      apiPrefix,
      referralProfileId,
      networkId,
      fetchPage,
      searchTerm,
      fetchRange,
      specialtyId || pageState.specialtyId,
      ageClassification || pageState.ageClassification
    );
    yield put(
      referralDashboardResultsReceived(
        referralProfileId,
        results.entities,
        fetchPage,
        results.totalPages,
        searchTerm,
        results.resultRange,
        results.resultSpecialtyId,
        results.resultAgeClassification,
        results.responseTime,
        results.network,
        results.organizationReferralRejected
      )
    );
  } catch (e) {
    yield put(referralDashboardResultsFetchFailed(e));
  } finally {
    if (yield cancelled()) {
      yield put(referralDashboardResultsFetchCancelled());
    }
  }
}

function* handlePageMounted(action) {
  yield call(fetchReferralDashboardResults, action.referralProfileId, action.networkId);
}

function* handleNextPageRequested(action) {
  const { currentPage, searchTerm } = yield select(
    state => state.referralDashboardResults.pageState
  );
  yield call(
    fetchReferralDashboardResults,
    action.referralProfileId,
    action.networkId,
    currentPage + 1,
    searchTerm
  );
}

function* handleSearchSubmitted(action) {
  yield call(
    fetchReferralDashboardResults,
    action.referralProfileId,
    action.networkId,
    1, // page number
    action.searchTerm,
    action.range,
    action.specialtyId,
    action.ageClassification
  );
}

function* handleFetchAction(action) {
  switch (action.type) {
    case REFERRAL_DASHBOARD_PAGE_MOUNTED:
      yield call(handlePageMounted, action);
      break;
    case REFERRAL_DASHBOARD_NEXT_PAGE_REQUESTED:
      yield call(handleNextPageRequested, action);
      break;
    case REFERRAL_DASHBOARD_SEARCH_SUBMITTED:
      yield call(handleSearchSubmitted, action);
      break;
    default:
    // no op
  }
}

function* handleReferralDashboardPageSlotsRequested(action) {
  yield put(referralDashboardPageSlotsFetching(action.resultId));

  const calendarStart = yield select(
    state => state.referralDashboardResults.entities.matchingResults[action.resultId].calendarStart
  );
  const days = action.type === REFERRAL_DASHBOARD_NEXT_PAGE_SLOTS_REQUESTED ? 5 : -5;
  const { apiPrefix } = yield select(state => state.configuration);

  const results = yield call(
    Services.ReferralDashboard.fetchPageSlotsForResult,
    apiPrefix,
    calendarStart,
    days,
    action.referralProfileId,
    action.resultId
  );

  yield put(referralDashboardPageSlotsReceived(action.resultId, results.data, results.time));
}

function* requireOutOfGroupReason(action) {
  const { onReasonProvided } = action;
  yield put(showModal({ name: 'outOfGroup' }));
  const { reason } = yield take(REFERRAL_DASHBOARD_OUT_OF_GROUP_REASON_PROVIDED);
  yield put(hideModal());
  yield call(onReasonProvided, reason);
}

export default function* ReferralDashboardSaga() {
  yield takeLatest(
    [
      REFERRAL_DASHBOARD_PAGE_MOUNTED,
      REFERRAL_DASHBOARD_NEXT_PAGE_REQUESTED,
      REFERRAL_DASHBOARD_SEARCH_SUBMITTED,
    ],
    handleFetchAction
  );
  yield takeLatest(
    [
      REFERRAL_DASHBOARD_NEXT_PAGE_SLOTS_REQUESTED,
      REFERRAL_DASHBOARD_PREVIOUS_PAGE_SLOTS_REQUESTED,
    ],
    handleReferralDashboardPageSlotsRequested
  );
  yield takeLatest(REFERRAL_DASHBOARD_OUT_OF_GROUP_REASON_REQUIRED, requireOutOfGroupReason);
}
